import moment from 'moment';
import * as React from 'react';
import Immutable from 'immutable';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import {
  DATAFIELDS_SCORE_URL,
  LEARN_HCAC2025_SIMULATION_RESULTS,
  LEARN_TUTORIAL_SIMULATION_RESULTS,
  ROBUSTNESS_SCORE_URL,
  WEEKLY_SCORE_URL,
} from '../../shared/constants';
import { addModifiers } from '../../shared/helpers';
import { formatMessageHelper } from '../../shared/helpers/internationalization';
import { ImmutableInterface } from '../../types';
import { PerformanceComparisonCompetition } from '../PerformanceComparison/PerformanceComparisonMain';

import { AlphaPerformanceSummary } from './AlphaPerformanceSummary';
import './AccordionTitle.scss';

export interface BaseAlphasPerformance {
  performanceAlphaSummary: Immutable.List<any>;
  performanceAlphaDatafieldsScore: Immutable.List<any>;
  performanceAlphaWeeklyScore: Immutable.List<any>;
  performanceAlphaRobustnessScore: Immutable.List<any>;
}

export interface BaseAlphasAccordionProps extends BaseAlphasPerformance {
  performanceAlphaSummaryTitle: string;
  performanceAlphaSummaryTitleValues?: Record<string, string | number>;

  performanceAlphaDatafieldsScoreTitle: string;

  performanceAlphaRobustnessScoreTitle: string;

  performanceAlphaWeeklyScoreTitle: string;

  competition: ImmutableInterface<PerformanceComparisonCompetition> | null;

  canRefresh: boolean;
  fetchingData: boolean;
  date: string;
  handleRefreshData: () => void;
}

interface AccordionTitleProps extends BaseAlphasAccordionProps {
  handlerShowAccordion: () => void;
  showContent: boolean;
}

function getPerformanceResultsTooltipUrl(competition: AccordionTitleProps['competition']) {
  if (competition?.get('id') === 'HCAC2025') {
    return LEARN_HCAC2025_SIMULATION_RESULTS;
  }

  return LEARN_TUTORIAL_SIMULATION_RESULTS;
}

const AccordionTitle = ({
  performanceAlphaSummaryTitle,
  performanceAlphaSummaryTitleValues,
  performanceAlphaSummary,
  performanceAlphaDatafieldsScoreTitle,
  performanceAlphaDatafieldsScore,
  performanceAlphaWeeklyScoreTitle,
  performanceAlphaWeeklyScore,
  performanceAlphaRobustnessScore,
  performanceAlphaRobustnessScoreTitle,
  competition,
  canRefresh,
  date,
  fetchingData,
  handleRefreshData,
  handlerShowAccordion,
  showContent,
  intl,
}: AccordionTitleProps & InjectedIntlProps) => (
  <React.Fragment>
    <div className="alpha-performance-summary-container">
      {performanceAlphaSummary?.size > 0 && (
        <AlphaPerformanceSummary
          title={performanceAlphaSummaryTitle}
          titleValues={performanceAlphaSummaryTitleValues}
          data={performanceAlphaSummary}
          tooltipUrl={getPerformanceResultsTooltipUrl(competition)}
        />
      )}
      {performanceAlphaRobustnessScore?.size > 0 && (
        <AlphaPerformanceSummary
          title={performanceAlphaRobustnessScoreTitle}
          titleValues={performanceAlphaSummaryTitleValues}
          data={performanceAlphaRobustnessScore}
          tooltipUrl={ROBUSTNESS_SCORE_URL}
        />
      )}
      {performanceAlphaDatafieldsScore?.size > 0 && (
        <AlphaPerformanceSummary
          title={performanceAlphaDatafieldsScoreTitle}
          titleValues={performanceAlphaSummaryTitleValues}
          data={performanceAlphaDatafieldsScore}
          tooltipUrl={DATAFIELDS_SCORE_URL}
        />
      )}
      {performanceAlphaWeeklyScore?.size > 0 && (
        <AlphaPerformanceSummary
          title={performanceAlphaWeeklyScoreTitle}
          titleValues={performanceAlphaSummaryTitleValues}
          data={performanceAlphaWeeklyScore}
          tooltipUrl={WEEKLY_SCORE_URL}
        />
      )}
    </div>

    {canRefresh && (
      <div className="correlation__content-status-time">
        <div className="correlation__content-status-time-title">{`${formatMessageHelper(intl, 'alphas.last.run')}:
        ${date ? moment(date).format('ddd, L, LT') : '-'}`}</div>
        <div
          id={fetchingData ? 'correlation__refresh-symbol' : ''}
          className="correlation__content-status-time-refresh"
          onClick={handleRefreshData}
        />
        <div
          onClick={handlerShowAccordion}
          className={addModifiers('correlation__content-status-arrow', showContent ? ['up'] : ['down'])}
        />
      </div>
    )}
  </React.Fragment>
);

export default injectIntl(AccordionTitle);
